import { Injectable } from '@angular/core';
import { FieldType } from './ag1-table.component';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable()
export class Ag1TableService {
  // subjects
  private isLoadingSubject = new BehaviorSubject<boolean>(false);
  private dataSubject = new BehaviorSubject<any[]>([]);
  private fieldsSubject = new BehaviorSubject<FieldType[]>([]);

  // observables
  isLoading$ = this.isLoadingSubject.asObservable();
  data$ = this.dataSubject.asObservable();
  fields$ = this.fieldsSubject.asObservable();

  constructor() {}

  // local storage
  public setLocalStorage(cacheName: string, fields: FieldType[]): void {
    localStorage.setItem(cacheName, JSON.stringify(fields));
  }

  public getLocalStorage(cacheName: string, fields: FieldType[]): FieldType[] {
    // get cache
    const localCache = JSON.parse(localStorage.getItem(cacheName));

    // get field keys
    const fieldKeys = fields.map((field) => field.name);

    // sanitize cached data
    let sanitisedCacheData: FieldType[] = [];
    if (localCache !== null) {
      sanitisedCacheData = localCache.filter((item) =>
        fieldKeys.includes(item?.name),
      );
    }

    if (
      Object.prototype.hasOwnProperty.call(localStorage, cacheName) &&
      sanitisedCacheData.length > 0
    ) {
      // loop through cached fields
      sanitisedCacheData.forEach((cachedField) => {
        // find matching field
        const matchingField = fields.find(
          (field) => field.name === cachedField.name,
        );

        // replace field with cached field
        if (matchingField) {
          // ensure cache contains valid data
          if (Object.prototype.hasOwnProperty.call(cachedField, 'order')) {
            matchingField.order = cachedField.order;
          }
          if (Object.prototype.hasOwnProperty.call(cachedField, 'checked')) {
            matchingField.checked = cachedField.checked;
          }
          if (Object.prototype.hasOwnProperty.call(cachedField, 'grouped')) {
            matchingField.grouped = cachedField.grouped;
          }
          if (Object.prototype.hasOwnProperty.call(cachedField, 'sort')) {
            matchingField.sort = cachedField.sort;
          }
          if (
            Object.prototype.hasOwnProperty.call(cachedField, 'sortDirection')
          ) {
            matchingField.sortDirection = cachedField.sortDirection;
          }
          if (Object.prototype.hasOwnProperty.call(cachedField, 'width')) {
            matchingField.width = cachedField.width;
          }
        }
      });
    }

    return fields;
  }

  // store
  public updateIsLoading(isLoading: boolean): void {
    this.isLoadingSubject.next(isLoading);
  }

  public updateData(data: any[]): void {
    // clone input in order to avoid mutation further down the line
    const clonedData = structuredClone(data);
    this.dataSubject.next(clonedData);
  }

  public updateFields(fields: FieldType[]): void {
    // clone input in order to avoid mutation further down the line
    const clonedFields = structuredClone(fields);
    this.fieldsSubject.next(clonedFields.sort((a, b) => a.order - b.order));
  }

  // track bys
  public trackByField(_index: number, field: FieldType): string {
    return field.name;
  }

  /**
   * Track-by for table rows. Used with virtual scrolling so Angular can uniquely identify rows
   * when items are added/removed from the DOM.
   *
   * Requirement: each row in the data passed to updateData() must have a unique, stable `id`
   * (number or string). UUIDs are fine if your data has no natural id. The id is for identity
   * only—sort order is determined by the array order, so the id does not need to be numerically
   * sortable. Without ids, rows can render in incorrect places.
   *
   * If item.id is missing, falls back to index (and logs once); this can cause wrong row content
   * with virtual scrolling and should be avoided.
   */
  public trackById(index: number, item: any): number | string {
    if (!item?.id) {
      // to avoid spam, only log the first item
      if (index === 0) {
        console.warn(
          'Item is missing an id, falling back to track by index. This may impact performance.',
          item,
        );
      }
      return index;
    }

    return item.id;
  }

  // sorting
  public sortDirection(field: FieldType): 'asc' | 'desc' | null {
    if (field.grouped && field.groupable) {
      switch (field?.sortDirection) {
        case 'asc':
          return 'desc';
        case 'desc':
          return 'asc';
        default:
          return 'asc';
      }
    }

    switch (field?.sortDirection) {
      case 'asc':
        return 'desc';
      case 'desc':
        return null;
      default:
        return 'asc';
    }
  }

  public sortDataLocally(): void {
    // sort data based on fields
    this.data$.pipe(take(1)).subscribe((data) => {
      this.fields$.pipe(take(1)).subscribe((fields: FieldType[]) => {
        if (!fields?.length || !data?.length) {
          return;
        }

        const fieldsAllowedToBeSorted = fields.filter(
          (field: FieldType) => field.sortDirection && field.order !== null,
        );

        if (!fieldsAllowedToBeSorted?.length) {
          return;
        }

        // Separate grouped and ungrouped fields
        const groupedFieldsToBeSorted = fieldsAllowedToBeSorted
          .filter(
            (field: FieldType) =>
              field.grouped && field.groupOrder !== null && field.groupable,
          )
          .sort((a, b) => a.groupOrder - b.groupOrder);

        const ungroupedFieldsToBeSorted = fieldsAllowedToBeSorted
          .filter((field: FieldType) => !field.grouped && field.checked)
          .sort((a, b) => a.order - b.order);

        const collator = new Intl.Collator(undefined, {
          sensitivity: 'base',
          numeric: true,
        });

        // avoid mutating original data
        const sortedData: any[] = structuredClone(data);

        sortedData.sort((a, b) => {
          if (groupedFieldsToBeSorted?.length) {
            // Process grouped fields first
            for (const field of groupedFieldsToBeSorted) {
              const comparison =
                field.sortDirection === 'asc'
                  ? collator.compare(a[field.name], b[field.name])
                  : collator.compare(b[field.name], a[field.name]);

              // Only continue to next field if values are equal
              if (comparison !== 0) {
                return comparison;
              }
            }
          } else {
            // Then process ungrouped fields
            for (const field of ungroupedFieldsToBeSorted) {
              const comparison =
                field.sortDirection === 'asc'
                  ? collator.compare(a[field.name], b[field.name])
                  : collator.compare(b[field.name], a[field.name]);

              // Only continue to next field if values are equal
              if (comparison !== 0) {
                return comparison;
              }
            }

            // If all fields are equal, maintain original order
            return 0;
          }
        });

        this.updateData(sortedData);
      });
    });
  }
}
